import PQueue from 'p-queue';
import { errorMessage, isAbortError, toError } from './toError';
import { retryUploadRequest } from './upload-retry';

/**
 * Startup budget: how long we wait for the FIRST byte to move before treating
 * the attempt as dead. Deliberately short so a dead/blocked connection fails
 * fast instead of hanging — nothing is in flight yet, so retrying is cheap.
 *
 * "Dead" is judged per CONNECTION, not per request (see ChunkActivityMemory):
 * a PUT that has moved nothing while a sibling PUT is streaming is starved, not
 * stalled, and is left alone.
 */
export const CHUNK_UPLOAD_STARTUP_TIMEOUT_MS = 15_000;
/**
 * Idle-timeout floor: once bytes ARE moving, how long the chunk may go with zero
 * further progress before we treat it as stalled and abort/retry.
 *
 * Kept well above the startup budget on purpose. A mobile radio handover (or a
 * tunnel) routinely pauses a live socket for 20–30s while `navigator.onLine`
 * stays true — so the offline park never engages — and aborting there re-PUTs
 * the whole part from byte 0 and usually just hits the same pause again. This
 * must exceed a realistic handover; it is NOT the "is the connection dead?"
 * question, which the startup budget above answers.
 */
export const CHUNK_UPLOAD_BASE_IDLE_TIMEOUT_MS = 45_000;
/** Idle-timeout ceiling: we never wait longer than this for movement, even on a very bursty link. */
export const CHUNK_UPLOAD_MAX_IDLE_TIMEOUT_MS = 90_000;
/**
 * The idle timeout adapts to the connection: it tolerates up to this multiple of
 * the largest legitimate progress gap seen so far on this chunk. A fast link keeps
 * a short leash (stays near the floor); a slow, bursty link earns a longer one.
 */
export const CHUNK_UPLOAD_IDLE_GAP_MULTIPLIER = 3;
/** Min gap between overall-progress emits — onprogress fires far more often than
 *  the bar needs to move, and each emit walks the MobX progress/stats path. */
export const PROGRESS_EMIT_THROTTLE_MS = 200;
/**
 * Retry budget for one part PUT, on top of the first attempt (8 attempts in
 * total). Chunk PUTs are idempotent and are where transient failures actually
 * happen on a long upload — an R2 5xx, a dropped socket, a genuine stall — so
 * they get a far more patient policy than the short JSON handshake calls, which
 * keep the `upload-retry` default. Backoff doubles from 1s and is capped so the
 * whole budget is roughly two minutes: long enough to ride out a hiccup, short
 * enough that a real outage still surfaces to the user.
 */
export const CHUNK_UPLOAD_MAX_RETRIES = 7;
export const CHUNK_UPLOAD_RETRY_INITIAL_DELAY_MS = 1_000;
export const CHUNK_UPLOAD_RETRY_MAX_DELAY_MS = 30_000;

/** A non-2xx PUT response. `status` lets the retry policy tell a deterministic
 *  rejection (expired signature, aborted multipart) from a transient one. */
export type ChunkHttpError = Error & { status: number };

/** What `uploadChunks` throws once a part has exhausted its retry budget. The
 *  message is what the failed-uploads UI reads (`describeUploadFailure`); the
 *  `cause` is the last attempt's message; `chunkIndex` pinpoints the part for
 *  the Sentry report; `status` is the last attempt's HTTP status, when it got
 *  one, so `isDeterministicChunkFailure` reads the same on the wrapper. */
export type ChunkUploadFailure = Error & { chunkIndex: number; status?: number };

export function isChunkUploadFailure(error: unknown): error is ChunkUploadFailure {
  return error instanceof Error && 'chunkIndex' in error && typeof error.chunkIndex === 'number';
}

/**
 * Presigned-URL failures that no amount of retrying fixes: 403 (signature
 * expired/invalid) and 404 (the multipart upload no longer exists). Everything
 * else — 5xx, 429, network errors, stalls — is worth another attempt.
 */
export function isDeterministicChunkFailure(error: unknown): boolean {
  if (!(error instanceof Error) || !('status' in error)) return false;
  const { status } = error as ChunkHttpError;
  return status === 403 || status === 404;
}

/** Bytes in part `index` of a file cut into `chunkSize` parts; the last part is
 *  the remainder. */
function partByteLength(index: number, chunkSize: number, fileSize: number): number {
  return Math.min((index + 1) * chunkSize, fileSize) - index * chunkSize;
}

/** Share (0–1) of the file that `completedParts` already hold. */
export function completedPartsShare(
  completedParts: Iterable<number>,
  chunkSize: number,
  fileSize: number,
): number {
  if (fileSize === 0) return 0;
  let bytes = 0;
  for (const index of new Set(completedParts)) bytes += partByteLength(index, chunkSize, fileSize);
  return Math.min(1, bytes / fileSize);
}

function createAbortError() {
  return new DOMException('Upload cancelled', 'AbortError');
}

export interface UploadChunkOptions {
  signal?: AbortSignal;
  /** Budget before the first byte moves. See CHUNK_UPLOAD_STARTUP_TIMEOUT_MS. */
  startupTimeoutMs?: number;
  /** Floor for the post-first-byte idle window. See CHUNK_UPLOAD_BASE_IDLE_TIMEOUT_MS. */
  baseIdleTimeoutMs?: number;
  /** Hard ceiling on the idle window, however bursty the link. */
  maxIdleTimeoutMs?: number;
  /**
   * Largest pause the CONNECTION has been observed to recover from so far (see
   * ChunkGapMemory). The adaptive leash starts here instead of 0, so an attempt
   * inherits what the link already proved rather than walking back into the
   * identical pause with a fresh, too-short leash.
   *
   * Scope is the caller's choice and is deliberately NOT per-chunk: the
   * quantity is a property of the link, not of a byte range.
   */
  initialMaxGapMs?: number;
  /** Reports this chunk's uploaded byte count as it climbs (for byte-accurate
   *  overall progress). Called with the monotonic `event.loaded`. */
  onBytes?: (loadedBytes: number) => void;
  /** Reports the largest gap THIS attempt recovered from, for the caller to fold
   *  into its gap memory. Fires on settle, success or failure. */
  onGapObserved?: (maxGapMs: number) => void;
  /**
   * Connection-scoped record of the last time ANY chunk moved bytes (see
   * ChunkActivityMemory). Consulted when this chunk's own timer fires: if the
   * connection was busy inside the window, this chunk was starved rather than
   * stalled and the timer is re-armed. Omit it and the chunk judges itself alone.
   */
  activity?: ChunkActivityMemory;
}

/**
 * Cross-chunk memory of when bytes last moved on this connection. Shared for
 * the same reason and with the same lifetime as ChunkGapMemory: it describes
 * the LINK, and every concurrent PUT — across files — shares one.
 *
 * Why it exists: with several PUTs in flight on a saturated uplink the browser
 * does not share the send window fairly. One or two streams progress while the
 * others sit with no `onprogress` events at all for long stretches. Judged in
 * isolation that silent stream looks dead and gets aborted, retried, starved
 * again and finally given up on — killing an upload whose bytes were flowing
 * the whole time. Judged against the connection it is obviously fine.
 *
 * Accepted consequence: a single genuinely hung stream on an otherwise busy
 * connection is not aborted until the connection quiets down (siblings finish,
 * the memory goes stale, the timer fires and aborts it). That is a slow tail,
 * never a deadlock — every chunk's timer keeps firing — and it errs on the side
 * of not killing a live transfer, the direction this subsystem errs in.
 */
export interface ChunkActivityMemory {
  /** `performance.now()` of the most recent byte movement on any chunk; null until the first. */
  lastByteActivityAt: number | null;
}

/**
 * Cross-call memory for the adaptive idle leash. A mutable box rather than a
 * return value because it outlives any single `uploadChunks` call: the leash
 * describes the CONNECTION, and every concurrent upload shares one.
 *
 * Owned by whoever owns the shared chunk queue (see `sharedQueue`) so the two
 * connection-scoped things have the same lifetime; `uploadChunks` falls back to
 * a private box when no queue is shared, which keeps standalone callers
 * isolated.
 */
export interface ChunkGapMemory {
  /**
   * Only ever grows, and only from gaps the link RECOVERED from. Two
   * consequences worth knowing, both accepted deliberately:
   *
   * - A concurrent part's recovery can vouch for a part that died in the same
   *   pause, seeding that part's retry with a leash long enough to have saved
   *   it. That is the intent — if 3 of 4 parts rode out a 50s pause, the pause
   *   was survivable and the 4th aborted too early.
   * - It never decays. One recovered 30s pause pins the leash near the 90s
   *   ceiling for the rest of the connection's uploads, so a genuinely dead PUT
   *   later takes ~270s (3 × 90s) rather than ~135s to fail out. Bounded by the
   *   ceiling and strictly on the side of not killing a live transfer, which is
   *   the direction this whole subsystem errs in; decay machinery would cost
   *   more than it buys.
   *
   * What it can NEVER do is learn from a pause that killed everything: an
   * unrecovered pause never closes, so no `onGapObserved` reports it.
   */
  maxRecoveredGapMs: number;
}

/**
 * Upload one part through XHR so an inactivity timeout can distinguish a slow
 * transfer (bytes still moving) from a request the browser left pending. The
 * presigned-URL request contract is otherwise identical to the previous fetch.
 */
export const uploadChunk = (
  url: string,
  file: File,
  index: number,
  chunkSize: number,
  {
    signal,
    startupTimeoutMs = CHUNK_UPLOAD_STARTUP_TIMEOUT_MS,
    baseIdleTimeoutMs = CHUNK_UPLOAD_BASE_IDLE_TIMEOUT_MS,
    maxIdleTimeoutMs = CHUNK_UPLOAD_MAX_IDLE_TIMEOUT_MS,
    initialMaxGapMs = 0,
    onBytes,
    onGapObserved,
    activity,
  }: UploadChunkOptions = {},
): Promise<void> => {
  const start = index * chunkSize;
  const end = Math.min((index + 1) * chunkSize, file.size);
  const blob = file.slice(start, end);

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    const xhr = new XMLHttpRequest();
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let lastUploadedBytes = 0;
    let settled = false;
    // Adaptive idle timeout: track the largest gap between progress events so a
    // slow/bursty connection earns a longer leash, while a fast one stays snappy.
    let lastProgressAt = performance.now();
    let maxGapMs = initialMaxGapMs;
    // Until the first byte moves we're answering "is this connection alive?",
    // which gets the short startup budget rather than the idle floor.
    let started = false;

    const cleanup = () => {
      if (idleTimer) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
      signal?.removeEventListener('abort', handleSignalAbort);
      xhr.upload.onprogress = null;
      xhr.onload = null;
      xhr.onerror = null;
      xhr.onabort = null;
    };

    const settle = (error?: Error | DOMException) => {
      if (settled) return;
      settled = true;
      cleanup();
      // Hand the caller what this attempt learned about the link. Note this only
      // ever contains gaps the connection RECOVERED from — the pause that stalls
      // us never closes, so it is never recorded. That's deliberate: a stall must
      // not teach the retry to tolerate a longer stall.
      onGapObserved?.(maxGapMs);
      if (error) reject(error);
      else resolve();
    };

    const abortWith = (error: Error | DOMException) => {
      settle(error);
      xhr.abort();
    };

    const currentIdleTimeoutMs = () =>
      started
        ? Math.min(
            maxIdleTimeoutMs,
            Math.max(baseIdleTimeoutMs, maxGapMs * CHUNK_UPLOAD_IDLE_GAP_MULTIPLIER),
          )
        : startupTimeoutMs;

    const armIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      const timeoutMs = currentIdleTimeoutMs();
      idleTimer = setTimeout(() => {
        // This chunk moved nothing for the whole window. Before calling that a
        // stall, ask the connection: if a sibling PUT moved bytes inside the
        // same window, this one is merely starved of the send window — leave it
        // be and look again after another window.
        const lastActivity = activity?.lastByteActivityAt ?? null;
        if (lastActivity !== null && performance.now() - lastActivity < timeoutMs) {
          armIdleTimer();
          return;
        }
        abortWith(new Error(`Upload chunk ${index} stalled for ${timeoutMs}ms`));
      }, timeoutMs);
    };

    function handleSignalAbort() {
      abortWith(createAbortError());
    }

    xhr.upload.onprogress = (event) => {
      if (event.loaded <= lastUploadedBytes) return;
      lastUploadedBytes = event.loaded;
      // Widen the leash by however long this gap was — the connection just proved
      // it can pause this long and still deliver, so don't abort a similar pause.
      const now = performance.now();
      maxGapMs = Math.max(maxGapMs, now - lastProgressAt);
      lastProgressAt = now;
      // Bytes are moving: leave the startup budget behind for the idle floor.
      started = true;
      if (activity) activity.lastByteActivityAt = now;
      onBytes?.(event.loaded);
      armIdleTimer();
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        settle();
        return;
      }
      const httpError: ChunkHttpError = Object.assign(
        new Error(`Failed to upload chunk ${index}: ${xhr.status}`, {
          cause: new Error(xhr.responseText),
        }),
        { status: xhr.status },
      );
      settle(httpError);
    };
    xhr.onerror = () => settle(new Error(`Network error uploading chunk ${index}`));
    xhr.onabort = () => settle(createAbortError());
    signal?.addEventListener('abort', handleSignalAbort, { once: true });

    try {
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      armIdleTimer();
      xhr.send(blob);
    } catch (error) {
      settle(toError(error));
    }
  });
};

export interface UploadChunksOptions {
  signal?: AbortSignal;
  /** Concurrency for the per-call fallback queue; ignored when `sharedQueue` is set. */
  maxConcurrentUploads?: number;
  /**
   * Optional queue shared across concurrent file uploads. When provided it caps
   * the *total* in-flight chunk PUTs globally; without it, each call gets its
   * own per-file queue (files × chunks could otherwise multiply and saturate the
   * connection, starving each other into stall-timeouts).
   */
  sharedQueue?: PQueue;
  /**
   * Adaptive-leash memory shared across every upload on this connection. Pass
   * the same box you pass `sharedQueue` with — both are connection-scoped, and
   * splitting their lifetimes is what made a fresh file re-learn a link that
   * the previous file had already characterised. Omit it and this call gets its
   * own private box (correct, just amnesiac between calls).
   */
  gapMemory?: ChunkGapMemory;
  /**
   * Last-byte-movement memory shared across every upload on this connection —
   * same box-passing contract as `gapMemory`, for the same reason. It is what
   * lets a silent chunk tell "the link is dead" from "a sibling is hogging the
   * link". Omit it and each call judges its chunks against its own siblings
   * only, which under-reports activity whenever several files share the queue.
   */
  activityMemory?: ChunkActivityMemory;
  /** Fires once, when the FIRST of this file's chunks actually gets a queue slot
   *  and begins — vs. sitting queued behind the shared cap. Lets the caller flip
   *  the file from "Queued" to "Uploading" only when bytes are about to move. */
  onStart?: () => void;
  /** Telemetry hook fired when a chunk has to fall back on the resilience layer:
   *  `offline-park` (waiting for the connection to return) or `retry` (an online
   *  transient error is being retried). Lets the caller record that this file
   *  needed help to survive (Sentry breadcrumb + batch ROI counters). */
  onResilienceEvent?: (
    kind: 'offline-park' | 'retry',
    info?: { chunkIndex?: number; attempt?: number; message?: string },
  ) => void;
  /**
   * Fires (throttled with the progress emits) whenever RAW bytes move on any
   * chunk — including movement the reported progress hides. The two signals
   * differ exactly during a retry catch-up: a re-PUT climbs from byte 0 while
   * `loadedPerChunk` holds the previous attempt's high-water mark, so the
   * aggregate doesn't advance until the retry passes its old peak (~18s for a
   * part that died at 90% on a 1 MB/s link). A stall detector fed only by
   * aggregate advances would call that healthy catch-up "stalled". This hook is
   * the raw-activity feed for such detectors; `progressCallback` stays the
   * display feed.
   */
  onByteActivity?: () => void;
  /**
   * Parts an earlier attempt at this same multipart upload already landed.
   * They are not sent again and count as uploaded from the first progress
   * report. Only valid with the same `urls` and `chunkSize` as that attempt.
   */
  completedParts?: ReadonlySet<number>;
  /** Fires once per part whose PUT succeeded, so the caller can resume later. */
  onPartUploaded?: (index: number) => void;
}

export const uploadChunks = async (
  urls: string[],
  file: File,
  chunkSize: number,
  progressCallback: (progress: number) => void = () => {},
  options: UploadChunksOptions = {},
) => {
  const {
    signal,
    maxConcurrentUploads = 8,
    sharedQueue,
    onStart,
    onResilienceEvent,
    onByteActivity,
    gapMemory = { maxRecoveredGapMs: 0 },
    activityMemory = { lastByteActivityAt: null },
    completedParts,
    onPartUploaded,
  } = options;
  let started = false;
  const chunkByteLength = (i: number) => partByteLength(i, chunkSize, file.size);
  // Byte-accurate progress: report (sum of every part's uploaded bytes) / total
  // file size, so the bar — and the speed/ETA derived from it — advance
  // continuously instead of jumping one whole chunk at a time. Throttled because
  // onprogress fires many times/sec across all in-flight parts.
  const loadedPerChunk = urls.map((_, i) => (completedParts?.has(i) ? chunkByteLength(i) : 0));
  // Adaptive-leash memory. See ChunkGapMemory for scope and the two accepted
  // consequences of a monotone shared counter.
  let lastEmitAt = 0;
  const emitProgress = (force: boolean) => {
    const now = performance.now();
    if (!force && now - lastEmitAt < PROGRESS_EMIT_THROTTLE_MS) return;
    lastEmitAt = now;
    const total = loadedPerChunk.reduce((sum, n) => sum + n, 0);
    // Safe to treat every emit as raw activity: emitProgress is only reachable
    // from a chunk's onBytes (which fires solely when event.loaded climbs) and
    // from a part's completion — both mean bytes genuinely landed just now.
    onByteActivity?.();
    progressCallback(file.size ? Math.min(1, total / file.size) : 1);
  };
  const queue = sharedQueue ?? new PQueue({ concurrency: maxConcurrentUploads });
  // Label for the retry / offline diagnostic warnings below.
  const tag = `[upload-chunk] "${file.name}"`;
  const pendingParts = urls.flatMap((url, index) =>
    completedParts?.has(index) ? [] : [{ url, index }],
  );
  const promises = pendingParts.map(({ url, index }) =>
    queue.add(async () => {
      // First of this file's chunks to get a slot → the file is genuinely
      // uploading now (not just queued behind the shared cap).
      if (!started) {
        started = true;
        onStart?.();
      }
      try {
        // Standard upload resilience: park while offline (don't burn retries at
        // a dead radio), then back off between online attempts. Chunk PUTs are
        // idempotent, so retrying is always safe. AbortError bails immediately.
        await retryUploadRequest(
          () =>
            uploadChunk(url, file, index, chunkSize, {
              signal,
              activity: activityMemory,
              initialMaxGapMs: gapMemory.maxRecoveredGapMs,
              onGapObserved: (gapMs) => {
                gapMemory.maxRecoveredGapMs = Math.max(gapMemory.maxRecoveredGapMs, gapMs);
              },
              onBytes: (loaded) => {
                // A retry re-PUTs from 0, so `loaded` drops then climbs again. Keep the
                // high-water mark rather than the raw value: the aggregate feeds
                // updateMediaProgress, which reads ANY regression as a whole-file retry
                // and wipes that file's measurement window (uploadStartedAt is cleared
                // and only ever re-seeded on a non-uploading → uploading transition, so
                // it would never come back). One chunk of several retrying must not
                // reset the whole file's speed/ETA.
                loadedPerChunk[index] = Math.max(loadedPerChunk[index], loaded);
                emitProgress(false);
              },
            }),
          {
            signal,
            maxRetries: CHUNK_UPLOAD_MAX_RETRIES,
            initialDelayMs: CHUNK_UPLOAD_RETRY_INITIAL_DELAY_MS,
            maxDelayMs: CHUNK_UPLOAD_RETRY_MAX_DELAY_MS,
            isDeterministicFailure: isDeterministicChunkFailure,
            onWait: () => {
              console.warn(`${tag} part ${index} offline — parking until connection returns`);
              onResilienceEvent?.('offline-park', { chunkIndex: index });
            },
            onRetry: (attempt, delay, err) => {
              console.warn(
                `${tag} part ${index} attempt ${attempt} failed, retrying in ${delay}ms — ${err.message}`,
              );
              onResilienceEvent?.('retry', { chunkIndex: index, attempt, message: err.message });
            },
          },
        );
      } catch (e) {
        if (isAbortError(e)) {
          throw e;
        }
        console.warn(
          `${tag} part ${index} gave up after ${CHUNK_UPLOAD_MAX_RETRIES + 1} attempts — ${errorMessage(e)}`,
        );
        const status =
          e instanceof Error && 'status' in e && typeof e.status === 'number'
            ? e.status
            : undefined;
        const failure: ChunkUploadFailure = Object.assign(
          new Error(`Failed to upload chunk ${index}`, { cause: errorMessage(e) }),
          { chunkIndex: index, status },
        );
        throw failure;
      }
      onPartUploaded?.(index);

      try {
        // Pin this part to its exact byte length so the total lands on 100% when
        // the last part completes (the final onprogress may undercount slightly).
        loadedPerChunk[index] = chunkByteLength(index);
        emitProgress(true);
      } catch (e) {
        console.error(`Unable to update upload progress for chunk ${index}`);
        console.error(e);
      }
    }),
  );
  await Promise.all(promises);
};
